import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 煤气化炉制合成气 P&ID 详细图：磨煤制浆 → 气化炉 → 激冷洗涤 → CO变换 → 低温甲醇洗 → 净化合成气 */
const media = {
  coal: { label: '水煤浆 / 干煤粉', light: '#4e342e', dark: '#a1887f', width: 2.6 },
  oxy: { label: '气化剂（O₂+蒸汽）', light: '#d32f2f', dark: '#e57373', width: 2 },
  syngas: { label: '粗合成气 / 净化气', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  ash: { label: '灰渣 / 细渣', light: '#616161', dark: '#b0bec5', width: 2 },
  chem: { label: 'CO₂ / 含硫尾气', light: '#00838f', dark: '#4dd0e1', width: 2 },
};

const devices = [
  { id: 'v101', kind: 'vDrum' as const, x: 70, y: 161, tag: 'V-101', name: '原煤仓' },
  { id: 'm101', kind: 'refiner' as const, x: 190, y: 186, tag: 'M-101', name: '磨煤机' },
  { id: 'p101', kind: 'pump' as const, x: 320, y: 193, tag: 'P-101', name: '水煤浆泵' },
  { id: 'r101', kind: 'reactor' as const, x: 500, y: 128, tag: 'R-101', name: '气化炉', note: '1300~1500℃·4.0~6.5MPa' },
  { id: 't101', kind: 'column' as const, x: 670, y: 94, tag: 'T-101', name: '激冷洗涤塔', note: '除渣除尘降温' },
  { id: 'r102', kind: 'reactor' as const, x: 840, y: 128, tag: 'R-102', name: 'CO变换炉', note: '调整氢碳比' },
  { id: 't102', kind: 'column' as const, x: 1010, y: 94, tag: 'T-102', name: '低温甲醇洗', note: '脱硫脱碳' },
];

const ports: PidPort[] = [
  { id: 'p-coal', label: '原煤', medium: 'coal', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-oxy', label: '气化剂（O₂+蒸汽）', medium: 'oxy', x: 300, y: 40, direction: 'in' as const },
  { id: 'p-ash', label: '灰渣 / 细渣', medium: 'ash', x: 590, y: 620, direction: 'out' as const },
  { id: 'p-co2', label: 'CO₂ / 含硫尾气', medium: 'chem', x: 1060, y: 52, direction: 'out' as const },
  { id: 'p-syngas', label: '净化合成气（CO+H₂）', medium: 'syngas', x: 1200, y: 224, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-coal', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'coal' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'm101', toSide: 'left', medium: 'coal' },
  { id: 's03', from: 'm101', fromSide: 'right', to: 'p101', toSide: 'left', medium: 'coal' },
  { id: 's04', from: 'p101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'coal', label: '水煤浆 / 煤粉' },
  { id: 's05', from: 'p-oxy', fromSide: 'right', to: 'r101', toSide: 'top', medium: 'oxy' },
  { id: 's06', from: 'r101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'syngas' },
  { id: 's07', from: 't101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'syngas' },
  { id: 's08', from: 'r102', fromSide: 'right', to: 't102', toSide: 'left', medium: 'syngas' },
  { id: 's09', from: 't102', fromSide: 'right', to: 'p-syngas', toSide: 'left', medium: 'syngas' },
  { id: 's10', from: 't102', fromSide: 'top', to: 'p-co2', toSide: 'left', medium: 'chem' },
  { id: 's11', from: 'r101', fromSide: 'bottom', to: 'p-ash', toSide: 'left', medium: 'ash' },
];

const annotations = [
  {
    title: '按炉型分类（三种气化工艺）',
    items: [
      '固定床（移动床）炉：块煤/碎煤自上而下与气化剂逆流，炉温较低（约900~1200℃），粗煤气含焦油酚类需配套回收处理；代表：鲁奇(Lurgi)、BGL——煤制天然气(CTG)路线采用',
      '流化床炉：碎煤悬浮流态化气化，约950~1100℃，单炉规模较小，工业应用较少；代表：温克勒(Winkler)、U-Gas',
      '气流床气化炉（本图所示）：煤粉/水煤浆与氧气并流高温气化，1300~1500℃、4.0~6.5 MPa，碳转化率≥98%，液态排渣；代表：GE水煤浆、多喷嘴、晋华炉、Shell/GSP干粉、航天炉(HT-L)',
    ],
  },
  {
    title: '按进料方式分类（气流床两大类）',
    items: [
      '水煤浆进料：原煤加水磨制成浆（浓度约60~65%），泵送入炉；氧煤比480~550 Nm³ O₂/t干煤；代表：GE、多喷嘴、晋华炉',
      '干煤粉进料：原煤磨粉并干燥（水分<2%），氮气/CO₂浓相输送；氧煤比约300~400 Nm³ O₂/t干煤；代表：Shell、GSP、航天炉(HT-L)',
    ],
  },
];

const CoalGasificationDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="煤气化装置详细流程"
    subtitle="原煤磨煤制浆 → 加压气流床气化（1300~1500℃、4.0~6.5 MPa、氧煤比480~550 Nm³ O₂/t干煤）→ 激冷洗涤 → CO变换 → 低温甲醇洗，产出甲醇 / 合成氨 / 制氢共用原料气"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
    annotations={annotations}
  />
);

export default CoalGasificationDiagram;
